<template>
  <Nav />
  <div class="container">
    <div class="fila-container">
      <div class="fila-header d-flex align-items-center gap-3">
        <a href="#" class="h3" @click.prevent="goBack">
          <i class="bi bi-arrow-left"></i>
        </a>
        <div>
          <h1>Fila de congelamento de dados</h1>
        </div>
      </div>

      <!-- Filtros -->
      <div class="fila-filters mb-4">
        <div class="row g-3">
          <div class="col-md-2">
            <label class="form-label small fw-semibold text-muted text-uppercase mb-1">Status</label>
            <select class="form-select form-select-sm" v-model="filters.status">
              <option value="">Todos</option>
              <option value="pendente">Pendente</option>
              <option value="processando">Processando</option>
              <option value="processado">Processado</option>
              <option value="congelado">Congelado</option>
              <option value="ignorado">Ignorado</option>
              <option value="erro">Erro</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label small fw-semibold text-muted text-uppercase mb-1">Município</label>
            <input
              type="text"
              class="form-control form-control-sm"
              placeholder="Nome do município"
              v-model="filters.municipio"
            />
          </div>
          <div class="col-md-2">
            <label class="form-label small fw-semibold text-muted text-uppercase mb-1">Cód. IBGE</label>
            <input
              type="text"
              class="form-control form-control-sm"
              placeholder="Ex: 3550308"
              v-model="filters.cod_ibge"
            />
          </div>
          <div class="col-md-2">
            <label class="form-label small fw-semibold text-muted text-uppercase mb-1">De</label>
            <input
              type="date"
              class="form-control form-control-sm"
              v-model="filters.criado_em_inicio"
            />
          </div>
          <div class="col-md-2">
            <label class="form-label small fw-semibold text-muted text-uppercase mb-1">Até</label>
            <input
              type="date"
              class="form-control form-control-sm"
              v-model="filters.criado_em_fim"
            />
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-12 d-flex gap-2">
            <button class="btn btn-secondary btn-sm px-4" @click="applyFilters">
              <i class="bi bi-funnel me-1"></i>Filtrar
            </button>
            <button class="btn btn-outline-secondary btn-sm px-3" @click="clearFilters">
              <i class="bi bi-x-circle me-1"></i>Limpar
            </button>
          </div>
        </div>
      </div>

      <!-- Tabela -->
      <div class="fila-body">
        <div class="table-responsive">
          <table class="table fila-table">
            <thead>
              <tr>
                <th scope="col">Município</th>
                <th scope="col">Cód. IBGE</th>
                <th scope="col">Exercício</th>
                <th scope="col">Status</th>
                <th scope="col">Criado em</th>
                <th scope="col">Congelado em</th>
                <th scope="col" class="actions-col">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="7" class="text-center text-muted py-4">
                  <span class="spinner-border spinner-border-sm me-2"></span>Carregando...
                </td>
              </tr>
              <tr v-else-if="records.length === 0">
                <td colspan="7" class="text-center text-muted py-4">Nenhum registro encontrado.</td>
              </tr>
              <tr v-for="record in records" :key="record.id" v-else>
                <td>{{ record.uf }} - {{ record.municipio }}</td>
                <td>{{ record.cod_ibge }}</td>
                <td>{{ record.exercicio }}</td>
                <td>
                  <span class="badge" :class="statusBadgeClass(record.status)">
                    {{ record.status }}
                  </span>
                </td>
                <td>{{ formatDate(record.criado_em) }}</td>
                <td>{{ record.congelado_em ? formatDate(record.congelado_em) : '—' }}</td>
                <td class="actions-col">
                  <div class="d-flex align-items-center justify-content-end">
                    <button
                      v-if="isRetryable(record.status)"
                      class="btn-icon me-2"
                      @click="openRetryConfirmModal(record)"
                      title="Reprocessar"
                    >
                      <i class="bi bi-arrow-clockwise text-warning"></i>
                    </button>
                    <button class="btn-icon" @click="openDetailModal(record)" title="Visualização detalhada">
                      <i class="bi bi-eye text-primary"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginação -->
        <div class="fila-pagination d-flex justify-content-between align-items-center mt-3" v-if="total > 0">
          <span class="text-muted small">
            Exibindo {{ records.length }} de {{ total }} registros
          </span>
          <div class="d-flex gap-2">
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="page === 1"
              @click="changePage(page - 1)"
            >Anterior</button>
            <span class="btn btn-sm disabled">{{ page }}</span>
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="page * limit >= total"
              @click="changePage(page + 1)"
            >Próxima</button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Visualização Detalhada -->
  <div class="modal fade" id="detalheCongelamentoModal" tabindex="-1" ref="detalheModal">
    <div class="modal-dialog modal-xl">
      <div class="modal-content" v-if="selectedRecord">
        <div class="modal-header">
          <h5 class="modal-title">Visualização Detalhada</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <div class="row g-3">
            <div class="col-md-6">
              <div class="detail-field">
                <span class="detail-label">ID</span>
                <span class="detail-value">{{ selectedRecord.id }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="detail-field">
                <span class="detail-label">Status</span>
                <span class="badge" :class="statusBadgeClass(selectedRecord.status)">
                  {{ selectedRecord.status }}
                </span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="detail-field">
                <span class="detail-label">Município</span>
                <span class="detail-value">{{ selectedRecord.municipio }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="detail-field">
                <span class="detail-label">UF</span>
                <span class="detail-value">{{ selectedRecord.uf }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="detail-field">
                <span class="detail-label">Cód. IBGE</span>
                <span class="detail-value">{{ selectedRecord.cod_ibge }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="detail-field">
                <span class="detail-label">Exercício</span>
                <span class="detail-value">{{ selectedRecord.exercicio }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="detail-field">
                <span class="detail-label">Criado em</span>
                <span class="detail-value">{{ formatDate(selectedRecord.criado_em) }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="detail-field">
                <span class="detail-label">Congelado em</span>
                <span class="detail-value">{{ selectedRecord.congelado_em ? formatDate(selectedRecord.congelado_em) : '—' }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="detail-field">
                <span class="detail-label">Run ID</span>
                <span class="detail-value detail-mono">{{ selectedRecord.run_id || '—' }}</span>
              </div>
            </div>
            <div class="col-12" v-if="selectedRecord.erro">
              <div class="detail-field">
                <span class="detail-label">Erro</span>
                <span class="detail-value text-danger">{{ selectedRecord.erro }}</span>
              </div>
            </div>
            <div class="col-12" v-if="selectedRecord.processamentos_incluidos && selectedRecord.processamentos_incluidos.length > 0">
              <div class="detail-field">
                <span class="detail-label">Processamentos Incluídos</span>
                <div class="table-responsive mt-1">
                  <table class="table table-sm processamentos-table">
                    <thead>
                      <tr>
                        <th scope="col">Entregável</th>
                        <th scope="col">Período</th>
                        <th scope="col">Protocolo</th>
                        <th scope="col">Fonte</th>
                        <th scope="col">Processado em</th>
                        <th scope="col">E-mail</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(proc, index) in selectedRecord.processamentos_incluidos" :key="index">
                        <td>{{ proc.entregavel || '—' }}</td>
                        <td>{{ proc.periodo ?? proc.mes ?? '—' }}</td>
                        <td class="detail-mono">{{ proc.protocolo || '—' }}</td>
                        <td>{{ proc.fonte || '—' }}</td>
                        <td>{{ formatDate(proc.data_processamento) }}</td>
                        <td>{{ Array.isArray(proc.email) ? proc.email.join(', ') : (proc.email || '—') }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Fechar</button>
          <button
            v-if="isRetryable(selectedRecord.status)"
            type="button"
            class="btn btn-warning"
            @click="openRetryConfirmModal(selectedRecord)"
          >
            <i class="bi bi-arrow-clockwise me-1"></i>Tentar novamente
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Confirmação de Retry -->
  <div class="modal fade" id="confirmRetryModal" tabindex="-1" ref="confirmRetryModal">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Confirmar reprocessamento</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <p>
            Tem certeza que deseja reprocessar o registro de
            <strong>{{ retryRecord?.uf }} - {{ retryRecord?.municipio }}</strong>
            (exercício {{ retryRecord?.exercicio }})?
          </p>
          <p class="text-muted small mb-0">
            Registros em erro voltam para a fila; registros já processados geram uma
            nova solicitação de congelamento, preservando o registro original.
          </p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal" :disabled="retrying">Cancelar</button>
          <button type="button" class="btn btn-warning" @click="confirmRetry" :disabled="retrying">
            <span v-if="retrying" class="spinner-border spinner-border-sm me-1"></span>
            Reprocessar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Nav from "@/components/Nav.vue";
import axiosApi from "@/axios";
import { mapState, mapActions } from "vuex";

export default {
  name: "FilaCongelamentoPlanilhas",
  components: { Nav },
  data() {
    return {
      records: [],
      total: 0,
      page: 1,
      limit: 50,
      loading: false,
      selectedRecord: null,
      retryRecord: null,
      retrying: false,
      filters: {
        status: "",
        municipio: "",
        cod_ibge: "",
        criado_em_inicio: "",
        criado_em_fim: "",
      },
    };
  },
  computed: {
    ...mapState(["user", "isAuthLoading"]),
  },
  async created() {
    try {
      await this.validateToken();

      if (this.isAuthLoading) {
        const checkAuth = () => {
          if (!this.isAuthLoading) {
            if (!this.user || this.user.role !== "admin") {
              this.$router.push("/");
            } else {
              this.fetchRecords();
            }
          } else {
            setTimeout(checkAuth, 100);
          }
        };
        checkAuth();
      } else {
        if (!this.user || this.user.role !== "admin") {
          this.$router.push("/");
        } else {
          this.fetchRecords();
        }
      }
    } catch {
      this.$router.push("/login");
    }
  },
  methods: {
    ...mapActions(["validateToken", "showToast"]),

    goBack() {
      this.$router.push("/filas");
    },

    async fetchRecords() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          limit: this.limit,
          ...Object.fromEntries(
            Object.entries(this.filters).filter(([, v]) => v !== "")
          ),
        };
        const response = await axiosApi.get("/api/filas/congelamento-planilhas", { params });
        this.records = response.data.data.data;
        this.total = response.data.data.total;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    applyFilters() {
      this.page = 1;
      this.fetchRecords();
    },

    clearFilters() {
      this.filters = {
        status: "",
        municipio: "",
        cod_ibge: "",
        criado_em_inicio: "",
        criado_em_fim: "",
      };
      this.page = 1;
      this.fetchRecords();
    },

    changePage(newPage) {
      this.page = newPage;
      this.fetchRecords();
    },

    openDetailModal(record) {
      this.selectedRecord = record;
      this.$nextTick(() => {
        const modal = new window.bootstrap.Modal(this.$refs.detalheModal);
        modal.show();
      });
    },

    // Espelha RETRYABLE_STATUSES do backend (model/filaCongelamentoPlanilhas.js):
    // só um desfecho terminal pode ser disparado de novo, e cada um tem seu
    // significado — 'erro' volta para a fila como está, 'processado' gera um
    // registro novo. 'pendente'/'processando' já estão em voo, e
    // 'congelado'/'ignorado' não são reprocessáveis manualmente.
    isRetryable(status) {
      return status === "erro" || status === "processado";
    },

    openRetryConfirmModal(record) {
      this.retryRecord = record;

      // Se o retry foi disparado de dentro do modal de detalhe, fecha-o antes
      // de abrir o de confirmação para não empilhar dois modais Bootstrap.
      const detalheInstance = window.bootstrap.Modal.getInstance(this.$refs.detalheModal);
      if (detalheInstance) detalheInstance.hide();

      this.$nextTick(() => {
        const modal = new window.bootstrap.Modal(this.$refs.confirmRetryModal);
        modal.show();
      });
    },

    async confirmRetry() {
      this.retrying = true;
      try {
        const response = await axiosApi.post("/api/filas/congelamento-planilhas/retry", {
          id: this.retryRecord.id,
        });

        // A mensagem já vem pronta do backend e distingue os dois desfechos
        // ('enfileirado' vs. 'criado'), então não há lógica duplicada aqui.
        this.showToast({
          title: "Sucesso",
          message: response.data.message,
          type: "toast-success",
          headerClass: "toast-header-success",
        });

        const instance = window.bootstrap.Modal.getInstance(this.$refs.confirmRetryModal);
        if (instance) instance.hide();

        this.retryRecord = null;
        this.selectedRecord = null;
      } catch (error) {
        console.error(error);
        this.showToast({
          title: "Erro",
          message: error.response?.data?.message || "Não foi possível reprocessar o registro.",
          type: "toast-danger",
          headerClass: "toast-header-danger",
        });
      } finally {
        // retrying volta antes do await: no caminho de erro o modal continua
        // aberto e o botão não pode ficar travado durante o refresh.
        this.retrying = false;
        // Refresh também no erro: 404 e 400 "status_invalido" significam que o
        // worker mexeu no registro por baixo da tabela, então é justamente aí
        // que a lista em tela está desatualizada.
        await this.fetchRecords();
      }
    },

    statusBadgeClass(status) {
      const classes = {
        pendente:    "bg-warning text-dark",
        processando: "bg-primary",
        processado:  "bg-success",
        congelado:   "bg-success",
        ignorado:    "bg-secondary",
        erro:        "bg-danger",
      };
      return classes[status] || "bg-secondary";
    },

    formatDate(value) {
      if (!value) return "—";
      // API já envia horário local do Brasil (sem conversão), apesar do
      // sufixo "Z" enganoso — extraímos os componentes literais da string
      // ao invés de deixar o Date() reinterpretar como UTC.
      const match = String(value).match(
        /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/
      );
      if (!match) return value;
      const [, year, month, day, hour, minute] = match;
      return `${day}/${month}/${year} ${hour}:${minute}`;
    },
  },
};
</script>

<style scoped>
.fila-container {
  padding: 20px 0;
  min-width: 1300px;
}

@media (max-width: 1260px) {
  .fila-container {
    min-width: 0;
    width: 100%;
  }
}

.fila-header h1 {
  font-size: 2rem;
  font-weight: 600;
  color: var(--azul-final);
}

.fila-table thead th {
  font-weight: 700;
  color: var(--azul-final);
  border-bottom: 2px solid #dee2e6;
}

.fila-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
}

.fila-table td {
  vertical-align: middle;
  color: #444;
}

.actions-col {
  text-align: right;
  width: 80px;
}

.btn-icon {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1em;
}

.btn-icon:hover {
  opacity: 0.7;
}

.detail-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-value {
  font-size: 0.9rem;
  color: #333;
}

.detail-mono {
  font-family: monospace;
  font-size: 0.8rem;
}

.processamentos-table {
  font-size: 0.85rem;
}

.processamentos-table thead th {
  font-weight: 600;
  color: #6c757d;
  border-bottom: 2px solid #dee2e6;
}

.processamentos-table td {
  vertical-align: middle;
}
</style>
